/**
 * Human-facing codes: pairing codes and ticket numbers.
 *
 * The alphabet deliberately excludes characters that are easy to confuse when
 * read off a screen and typed on a tablet keyboard (0/O, 1/I/L, 5/S, 2/Z, 8/B).
 */
export const UNAMBIGUOUS_ALPHABET = '34679ACDEFGHJKMNPQRTUVWXY';
export const webCryptoRandom = {
    bytes(size) {
        const out = new Uint8Array(size);
        // globalThis.crypto exists in Node >= 19 and every supported browser.
        globalThis.crypto.getRandomValues(out);
        return out;
    },
};
/**
 * Rejection sampling keeps the distribution uniform. A plain `% alphabet.length`
 * would bias the first characters of the alphabet, which matters because these
 * codes are a (short-lived) authentication factor for device pairing.
 */
export function randomCode(length, random = webCryptoRandom) {
    const alphabet = UNAMBIGUOUS_ALPHABET;
    const limit = Math.floor(256 / alphabet.length) * alphabet.length;
    let out = '';
    while (out.length < length) {
        const chunk = random.bytes(Math.max(8, (length - out.length) * 2));
        for (const b of chunk) {
            if (b >= limit)
                continue;
            out += alphabet[b % alphabet.length];
            if (out.length === length)
                break;
        }
    }
    return out;
}
/** Pairing code in the documented `8FQ4-19KD` shape. */
export function generatePairingCode(random = webCryptoRandom) {
    return `${randomCode(4, random)}-${randomCode(4, random)}`;
}
const PAIRING_CODE_RE = new RegExp(`^[${UNAMBIGUOUS_ALPHABET}]{4}-[${UNAMBIGUOUS_ALPHABET}]{4}$`);
/**
 * Normalises user input before comparison: uppercase, strip spaces, insert the
 * dash if the visitor typed the code without it, and map the characters people
 * habitually substitute (O to 0 is not possible here because 0 is not in the
 * alphabet - so O maps to nothing and the code is simply rejected).
 */
export function normalizePairingCode(input) {
    const cleaned = input
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .replace(/O/g, '0')
        .replace(/[IL]/g, '1')
        .replace(/S/g, '5')
        .replace(/Z/g, '2')
        .replace(/B/g, '8');
    if (cleaned.length !== 8)
        return cleaned;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
}
export function isValidPairingCode(input) {
    return PAIRING_CODE_RE.test(input);
}
/**
 * Daily ticket number shown to the visitor and to staff, e.g. `A-014`.
 * The letter rotates per weekday so two consecutive days are hard to confuse.
 */
export function formatTicketCode(sequence, date = new Date()) {
    const letters = 'ABCDEFG';
    const dayIndex = (date.getUTCDay() + 6) % 7;
    const letter = letters[dayIndex] ?? 'A';
    const n = ((sequence - 1) % 999) + 1;
    return `${letter}-${String(n).padStart(3, '0')}`;
}
/**
 * Stable, sortable device identifier as documented (`TAB-SIEGEN-EG-001`).
 * Falls back gracefully when the location name contains umlauts or spaces.
 */
export function buildDeviceCode(locationName, deviceName, sequence) {
    const slug = (s) => s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '')
        .toUpperCase()
        .slice(0, 10) || 'X';
    return `TAB-${slug(locationName)}-${slug(deviceName)}-${String(sequence).padStart(3, '0')}`;
}
//# sourceMappingURL=codes.js.map